import { ReactNode, forwardRef, HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export type LabelColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';
export type LabelVariant = 'filled' | 'outlined' | 'soft';

interface LabelProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  color?: LabelColor;
  variant?: LabelVariant;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

const colorClasses: Record<LabelColor, Record<LabelVariant, string>> = {
  default: {
    filled: 'bg-muted text-muted-foreground',
    outlined: 'border border-muted-foreground text-muted-foreground',
    soft: 'bg-muted/50 text-muted-foreground'
  },
  primary: {
    filled: 'bg-primary text-primary-foreground',
    outlined: 'border border-primary text-primary dark:text-secondary',
    soft: 'bg-primary/10 text-primary dark:text-secondary'
  },
  secondary: {
    filled: 'bg-secondary text-secondary-foreground',
    outlined: 'border border-secondary text-secondary-foreground',
    soft: 'bg-secondary/50 text-secondary-foreground'
  },
  info: {
    filled: 'bg-blue-500 text-white',
    outlined: 'border border-blue-500 text-blue-500',
    soft: 'bg-blue-500/10 text-blue-500'
  },
  success: {
    filled: 'bg-green-500 text-white',
    outlined: 'border border-green-500 text-green-500',
    soft: 'bg-green-500/10 text-green-500'
  },
  warning: {
    filled: 'bg-yellow-500 text-white',
    outlined: 'border border-yellow-500 text-yellow-500',
    soft: 'bg-yellow-500/10 text-yellow-500'
  },
  error: {
    filled: 'bg-destructive text-destructive-foreground',
    outlined: 'border border-destructive text-destructive',
    soft: 'bg-destructive/10 text-destructive'
  }
};

const Label = forwardRef<HTMLSpanElement, LabelProps>(
  (
    { children, color = 'default', variant = 'soft', startIcon, endIcon, className, ...other },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
          colorClasses[color][variant],
          className
        )}
        {...other}
      >
        {startIcon && <span className="-ml-0.5">{startIcon}</span>}
        {children}
        {endIcon && <span className="-mr-0.5">{endIcon}</span>}
      </span>
    );
  }
);

Label.displayName = 'Label';

export default Label;
